import axios from 'axios';

// Token handling function
const getToken = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('jwt');
    
    if (urlToken) {
      // If token is in URL, save it to localStorage for future use
      localStorage.setItem('mobilityToken', urlToken);
      return urlToken;
    }
    
    // Otherwise, try to get it from localStorage
    const storedToken = localStorage.getItem('mobilityToken');
    
    if (storedToken) {
      return storedToken;
    }
    
    return null;
  } catch (err) {
    console.error("Error getting token:", err);
    return null;
  }
};
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SSO_LOGIN_URL = process.env.REACT_APP_SSO_LOGIN_URL;

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - adds token to requests
api.interceptors.request.use(
  config => {
    try {
      const token = getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      console.log(`Request to ${config.url} with auth: ${token ? 'Yes' : 'No'}`);
      return config;
    } catch (err) {
      console.error("Error in request interceptor:", err);
      return config;
    }
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles 401 Unauthorized errors
api.interceptors.response.use(
  // Success handler
  (response) => {
    return response;
  },
  // Error handler
  (error) => {
    if (error.response && error.response.status === 401) {
      console.log('Token invalid or expired, redirecting to login');
      
      localStorage.removeItem('mobilityToken');
      
      const returnToUrl = encodeURIComponent(FRONTEND_URL);
      
      window.location.href = `${SSO_LOGIN_URL}?returnTo=${returnToUrl}`;
      
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to extract error message from various response formats
const extractErrorMessage = (error) => {
  try {
    if (error.response && error.response.data) {
      const data = error.response.data;
      
      // Check for message field
      if (data.message) {
        return data.message;
      }
      
      if (data.status && typeof data.status === 'string' && data.status.includes(':')) {
        return data.status.split(':')[1].trim();
      }
      
      if (typeof data === 'string') {
        return data;
      }
      
      // Return stringified data as last resort
      return JSON.stringify(data);
    }
    
    // Return the error message if available, otherwise a generic message
    return error.message || 'An unknown error occurred';
  } catch (err) {
    console.error("Error extracting error message:", err);
    return "An unknown error occurred";
  }
};

export const guaranteeService = {
  // Get list of all guarantees with no filters
  getAll: async () => {
    try {
      console.log(`Fetching all guarantees`);
      const response = await api.get('');
      return response.data;
    } catch (error) {
      console.error('Error fetching guarantees:', error);
      throw {
        message: extractErrorMessage(error),
        originalError: error
      };
    }
  },
  search: async (queryParams) => {
    try {
      console.log(`Searching guarantees with params:`, Object.fromEntries(queryParams));
      const response = await api.get('/search', { params: Object.fromEntries(queryParams) });
      return response.data;
    } catch (error) {
      console.error('Error searching guarantees:', error);
      throw {
        message: extractErrorMessage(error),
        originalError: error
      };
    }
  },
  // Get filtered guarantees with date parameters
  getFiltered: async (fromDate, toDate) => {
    try {
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      
      console.log('Fetching filtered guarantees with params:', params);
      
      // Use the params option in axios to properly format the URL parameters
      const response = await api.get('/filtered', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered guarantees:', error);
      throw {
        message: extractErrorMessage(error),
        originalError: error
      };
    }
  },
  
  // Get a specific guarantee by confirmation code
  getById: async (confirmationCode) => {
    try {
      console.log(`Fetching guarantee with code: ${confirmationCode}`);
      const response = await api.get(`/${confirmationCode}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching guarantee details:', error);
      throw {
        message: extractErrorMessage(error),
        originalError: error
      };
    }
  },
  
  // Create a new guarantee
  create: async (guaranteeData) => {
    try {
      console.log("Creating new guarantee");
      const response = await api.post('', guaranteeData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating guarantee:', error);
      return {
        success: false,
        data: error.response?.data || {},
        message: extractErrorMessage(error)
      };
    }
  },
  
  // Update an existing guarantee
  update: async (confirmationCode, guaranteeData) => {
    try {
      console.log(`Updating guarantee with code: ${confirmationCode}`);
      const response = await api.put(`/${confirmationCode}`, guaranteeData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating guarantee:', error);
      return {
        success: false,
        data: error.response?.data || {},
        message: extractErrorMessage(error)
      };
    }
  },
  
  // Delete a guarantee
  delete: async (confirmationCode) => {
    try {
      console.log(`Deleting guarantee with code: ${confirmationCode}`);
      const response = await api.delete(`/${confirmationCode}`);
      console.log("Delete successful:", response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Delete error:", error);
      if (error.response) {
        console.error("Error status:", error.response.status);
        console.error("Error data:", error.response.data);
      }
      return {
        success: false,
        data: error.response?.data || {},
        message: extractErrorMessage(error)
      };
    }
  },
  
  // Get PDF for a guarantee
  getPdf: async (confirmationCode, template = 'default') => {
    try {
      console.log(`Generating PDF for guarantee with code: ${confirmationCode}`);
      const response = await api.get(`/${confirmationCode}/pdf?template=${template}`, {
        responseType: 'blob'
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mobility-guarantee-${confirmationCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error generating PDF:', error);
      return {
        success: false,
        message: extractErrorMessage(error)
      };
    }
  },
  
  // Get pricing information
  getPrices: async (duration = 365) => {
    try {
      console.log(`Fetching prices for duration: ${duration}`);
      const response = await api.get(`/prices/${duration}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching prices:', error);
      throw {
        message: extractErrorMessage(error),
        originalError: error
      };
    }
  }
};

export default api;